<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import CompanyAPI from 'dashboard/api/companies';
import ContactAPI from 'dashboard/api/contacts';
import camelcaseKeys from 'camelcase-keys';

import LinkedEntitySection from 'dashboard/components-next/LinkedEntitySection.vue';

const props = defineProps({
  contactId: { type: Number, required: true },
  companies: { type: Array, default: () => [] },
});

const emit = defineEmits(['companyUpdated']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const searchResults = ref([]);
const isSearching = ref(false);
const isUpdating = ref(false);
const selectedCandidate = ref(null);
const searchQuery = ref('');

const companyOptions = computed(() =>
  searchResults.value
    .filter(c => !props.companies.some(linked => linked.id === c.id))
    .map(c => ({
      value: c.id,
      label: [c.name, c.domain].filter(Boolean).join(' · '),
    }))
);

const emptyState = computed(() => {
  if (isSearching.value) return t('CONTACTS_LAYOUT.DETAILS.COMPANY.LOADING');
  return t('CONTACTS_LAYOUT.DETAILS.COMPANY.NO_RESULTS');
});

const doSearch = async query => {
  const q = query.trim();
  isSearching.value = true;
  try {
    const { data } = q
      ? await CompanyAPI.search(q)
      : await CompanyAPI.get({ sort: 'name' });
    searchResults.value = camelcaseKeys(data?.payload || [], { deep: true });
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

onMounted(() => doSearch(''));

const debouncedSearch = debounce(doSearch, 300);

const handleSearch = query => {
  searchQuery.value = query;
  debouncedSearch(query);
};

const handleCompanySelect = companyId => {
  const found = searchResults.value.find(c => c.id === Number(companyId));
  if (found) selectedCandidate.value = found;
};

const clearSelectedCandidate = () => {
  selectedCandidate.value = null;
  searchQuery.value = '';
  doSearch('');
};

const confirmAssign = async () => {
  if (!selectedCandidate.value || isUpdating.value) return;
  isUpdating.value = true;
  try {
    await ContactAPI.assignCompany(selectedCandidate.value.id, props.contactId);
    await store.dispatch('contacts/show', { id: props.contactId });
    emit('companyUpdated');
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.ASSIGN_SUCCESS'));
    clearSelectedCandidate();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.ASSIGN_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const removeCompany = async companyId => {
  if (isUpdating.value) return;
  isUpdating.value = true;
  try {
    await ContactAPI.removeCompany(companyId, props.contactId);
    await store.dispatch('contacts/show', { id: props.contactId });
    emit('companyUpdated');
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE_SUCCESS'));
    doSearch('');
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

// Dentro de uma pagina com LinkedEntityPane, abre a empresa na propria coluna.
const openLinkedInPlace = inject('openLinkedInPlace', null);

const goToCompany = companyId => {
  if (openLinkedInPlace) {
    openLinkedInPlace('company', companyId);
    return;
  }
  router.push({
    name: 'companies_dashboard_show',
    params: { accountId: route.params.accountId, companyId },
  });
};

const sectionLabels = computed(() => ({
  addTitle: t('CONTACTS_LAYOUT.DETAILS.COMPANY.ADD_TITLE'),
  addDescription: t('CONTACTS_LAYOUT.DETAILS.COMPANY.ADD_DESCRIPTION'),
  searchPlaceholder: t('CONTACTS_LAYOUT.DETAILS.COMPANY.SEARCH_PLACEHOLDER'),
  emptySearch: emptyState.value,
  confirmTitle: t('CONTACTS_LAYOUT.DETAILS.COMPANY.CONFIRM_TITLE'),
  confirmDescription: t('CONTACTS_LAYOUT.DETAILS.COMPANY.CONFIRM_DESCRIPTION'),
  confirm: t('CONTACTS_LAYOUT.DETAILS.COMPANY.CONFIRM_ADD'),
  cancel: t('CONTACTS_LAYOUT.DETAILS.COMPANY.CANCEL'),
  sectionTitle: t('CONTACTS_LAYOUT.DETAILS.COMPANY.TITLE'),
  empty: t('CONTACTS_LAYOUT.DETAILS.COMPANY.EMPTY'),
  open: t('CONTACTS_LAYOUT.DETAILS.COMPANY.OPEN'),
  remove: t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE'),
}));

const items = computed(() =>
  props.companies.map(company => ({
    id: company.id,
    name: company.name,
    subtitle: company.domain,
    avatarUrl: company.avatarUrl,
  }))
);

const candidate = computed(() =>
  selectedCandidate.value
    ? {
        name: selectedCandidate.value.name,
        subtitle: selectedCandidate.value.domain,
        avatarUrl: selectedCandidate.value.avatarUrl,
      }
    : null
);
</script>

<template>
  <LinkedEntitySection
    :items="items"
    :candidate="candidate"
    :options="companyOptions"
    :labels="sectionLabels"
    :is-busy="isUpdating"
    @search="handleSearch"
    @select="handleCompanySelect"
    @cancel="clearSelectedCandidate"
    @confirm="confirmAssign"
    @open="goToCompany"
    @remove="removeCompany"
  />
</template>
